<script setup lang="ts">
import { allocationMonthKey, createTaskSchema, TASK_MIN_HOURS } from '@schwrz/shared';
import { computed, ref, watch } from 'vue';

import { useAssigneeCapacity } from '../../shared/composables/useAssigneeCapacity';
import { AppButton, AppHint, AppInput } from '../../shared/ui';
import { useAuthStore } from '../../stores/auth';
import { useClientsStore } from '../../stores/clients';
import { useProjectsStore } from '../../stores/projects';
import { useTasksStore } from '../../stores/tasks';
import { useWorkspaceUsersStore } from '../../stores/workspaceUsers';
import ClientProjectSelect from '../projects/ClientProjectSelect.vue';

import DueDateField from './DueDateField.vue';

const QUICK_ADD_LAST_PROJECT_KEY = 'quickAddLastProjectId';

const props = defineProps<{ open: boolean }>();
const emit = defineEmits<{ close: [] }>();

const auth = useAuthStore();
const tasksStore = useTasksStore();
const projectsStore = useProjectsStore();
const clientsStore = useClientsStore();
const usersStore = useWorkspaceUsersStore();
const capacityMonthKey = computed(() => allocationMonthKey(dueDate.value || null));
const { refresh, assigneeLabel } = useAssigneeCapacity(capacityMonthKey);

const title = ref('');
const projectId = ref('');
const clientId = ref('');
const dueDate = ref('');
const dueTime = ref<string | null>(null);
const timeHours = ref(String(TASK_MIN_HOURS));
const submitting = ref(false);
const error = ref<string | null>(null);

function resolvePrefill(): { clientId: string; projectId: string } {
  const selectedProject = projectsStore.selected;
  if (selectedProject && !selectedProject.archived) {
    const client = clientsStore.getByRef(selectedProject.clientId);
    if (client && !client.archived) {
      return { clientId: selectedProject.clientId, projectId: selectedProject.id };
    }
  }

  const selectedClient = clientsStore.selected;
  if (selectedClient && !selectedClient.archived) {
    const first = projectsStore.ofClient(selectedClient.id).find((p) => !p.archived);
    if (first) {
      return { clientId: selectedClient.id, projectId: first.id };
    }
  }

  const saved = localStorage.getItem(QUICK_ADD_LAST_PROJECT_KEY);
  if (saved) {
    const project = projectsStore.items.find((p) => p.id === saved && !p.archived);
    if (project) {
      const client = clientsStore.getByRef(project.clientId);
      if (client && !client.archived) {
        return { clientId: project.clientId, projectId: project.id };
      }
    }
  }

  for (const client of clientsStore.sorted) {
    if (client.archived) continue;
    const live = projectsStore.ofClient(client.id).filter((p) => !p.archived);
    if (live.length > 0) {
      const first = live[0];
      return {
        clientId: client.id,
        projectId: live.length === 1 && first ? first.id : '',
      };
    }
  }

  return { clientId: '', projectId: '' };
}

watch(
  () => props.open,
  async (isOpen) => {
    if (!isOpen) return;
    title.value = '';
    dueDate.value = '';
    dueTime.value = null;
    timeHours.value = String(TASK_MIN_HOURS);
    error.value = null;
    await Promise.all([
      projectsStore.load(),
      clientsStore.load(),
      usersStore.load(),
      refresh(),
    ]);
    const prefill = resolvePrefill();
    clientId.value = prefill.clientId;
    projectId.value = prefill.projectId;
  },
);

const assigneeId = computed(() => auth.user?.workspaceUserId ?? null);

const assignableUsers = computed(() => {
  const proj = projectsStore.items.find((p) => p.id === projectId.value);
  return usersStore.assignableForClient(proj?.clientId ?? null);
});

async function onSubmit(): Promise<void> {
  error.value = null;
  if (!projectId.value) {
    error.value = 'Выберите проект';
    return;
  }
  const payload = {
    projectId: projectId.value,
    title: title.value.trim(),
    assignedToId: assigneeId.value,
    dueDate: dueDate.value || null,
    dueTime: dueTime.value,
    timeHours: Number(timeHours.value),
  };
  const parsed = createTaskSchema.safeParse(payload);
  if (!parsed.success) {
    error.value = parsed.error.issues[0]?.message ?? 'Проверьте поля';
    return;
  }
  submitting.value = true;
  try {
    await tasksStore.create(parsed.data);
    localStorage.setItem(QUICK_ADD_LAST_PROJECT_KEY, projectId.value);
    await refresh();
    emit('close');
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Не удалось создать задачу';
    await refresh();
  } finally {
    submitting.value = false;
  }
}
</script>

<template>
  <Teleport to="body">
    <div
      v-if="open"
      class="quick-add__backdrop"
      @click.self="emit('close')"
    >
      <form
        class="quick-add__dialog"
        role="dialog"
        aria-modal="true"
        aria-label="Быстрое добавление задачи"
        @submit.prevent="onSubmit"
      >
        <h3 class="quick-add__title">
          Новая задача
        </h3>
        <AppInput
          v-model="title"
          type="text"
          placeholder="Название"
          aria-label="Название"
          required
          autofocus
        />
        <ClientProjectSelect
          v-model="projectId"
          v-model:client-id="clientId"
        />
        <DueDateField
          v-model="dueDate"
          v-model:due-time="dueTime"
          with-time
        />
        <AppInput
          v-model="timeHours"
          type="number"
          inputmode="decimal"
          :min="TASK_MIN_HOURS"
          step="0.5"
          aria-label="Часы"
        />
        <p
          v-if="assigneeId"
          class="quick-add__assignee"
        >
          Исполнитель:
          {{
            assignableUsers.find((u) => u.id === assigneeId)
              ? assigneeLabel(assignableUsers.find((u) => u.id === assigneeId)!)
              : '—'
          }}
        </p>
        <div class="quick-add__actions">
          <AppButton
            type="submit"
            variant="primary"
            :loading="submitting"
          >
            Добавить
          </AppButton>
          <AppButton
            type="button"
            variant="ghost"
            :disabled="submitting"
            @click="emit('close')"
          >
            Отмена
          </AppButton>
        </div>
        <AppHint
          v-if="error"
          variant="danger"
        >
          {{ error }}
        </AppHint>
      </form>
    </div>
  </Teleport>
</template>

<style scoped>
.quick-add__backdrop {
  position: fixed;
  inset: 0;
  z-index: 1200;
  background: rgba(0, 0, 0, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.quick-add__dialog {
  width: min(440px, 100%);
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  padding: 1rem;
  border-radius: 12px;
  background: var(--surface, #fff);
  border: 1px solid var(--divider);
}

.quick-add__title {
  margin: 0;
  font-family: var(--font-ui);
  font-size: 1rem;
  font-weight: 600;
}

.quick-add__assignee {
  margin: 0;
  font-size: 0.8rem;
  color: var(--muted);
}

.quick-add__actions {
  display: flex;
  gap: 0.5rem;
}
</style>
